import React from "react";

import {
  useDeleteSdkBundle,
  useDeleteSdkConfig,
  useSdkBundles,
  useSdkCatalog,
  useSdkConfigs,
} from "src/api/connectApi/queries";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { Spinner } from "src/base-components/Spinner";
import { getSdkBundleErrorDescription } from "src/connections/sdk/bundleErrors";
import { SdkBundleModal } from "src/connections/sdk/SdkBundleModal";
import { SdkScriptConfigForm } from "src/connections/sdk/SdkScriptConfigForm";
import { SdkScriptModal } from "src/connections/sdk/SdkScriptModal";
import {
  useSdkManagementActions,
  useSdkModal,
} from "src/connections/sdk/sdkManagementStore";
import { Modal } from "src/design-system/Modal";
import { NodeIcon } from "src/design-system/NodeIcon";
import { toastActions } from "src/design-system/Toast/utils";

type Props = {
  baseUrl: string | undefined;
};

export const SdkManagementModals: React.FC<Props> = ({ baseUrl }) => {
  const modal = useSdkModal();
  const { closeModal } = useSdkManagementActions();
  const configs = useSdkConfigs(baseUrl);
  const bundles = useSdkBundles(baseUrl);
  const catalog = useSdkCatalog(baseUrl);

  const scriptToEdit = modal.kind === "editScript" ? modal.script : undefined;
  const editCatalogEntry =
    scriptToEdit &&
    catalog.data?.find(
      (entry) =>
        entry.provider === scriptToEdit.provider &&
        entry.provider_sdk_version === scriptToEdit.provider_sdk_version,
    );
  const deleteBundle = useDeleteSdkBundle(baseUrl);
  const deleteScript = useDeleteSdkConfig(baseUrl);

  const handleDeleteScript = async () => {
    if (modal.kind !== "deleteScript") return;
    const { id, name } = modal.script;
    try {
      await deleteScript.mutateAsync(id);
      toastActions.success({
        title: "SDK Script deleted",
        description: `${name} was deleted.`,
      });
      closeModal();
    } catch (error) {
      toastActions.failure({
        title: "Failed to delete SDK Script",
        description: getSdkBundleErrorDescription(
          error,
          `${name} could not be deleted.`,
        ),
      });
    }
  };

  const handleDeleteBundle = async () => {
    if (modal.kind !== "deleteBundle") return;
    const { id, name } = modal.bundle;
    try {
      await deleteBundle.mutateAsync(id);
      toastActions.success({
        title: "SDK Bundle deleted",
        description: `Bundle ${name} was deleted.`,
      });
      closeModal();
    } catch (error) {
      toastActions.failure({
        title: "Failed to delete SDK Bundle",
        description: getSdkBundleErrorDescription(
          error,
          `Bundle ${name} could not be deleted.`,
        ),
      });
    }
  };

  return (
    <>
      <SdkBundleModal
        baseUrl={baseUrl}
        bundle={modal.kind === "editBundle" ? modal.bundle : undefined}
        configs={configs.data ?? []}
        open={modal.kind === "editBundle"}
        onClose={closeModal}
      />

      <SdkScriptModal
        baseUrl={baseUrl}
        bundles={bundles.data ?? []}
        configs={configs.data ?? []}
        open={modal.kind === "addScriptToBundle"}
        script={modal.kind === "addScriptToBundle" ? modal.script : undefined}
        onClose={closeModal}
      />

      {scriptToEdit && (
        <Modal open onClose={closeModal}>
          <Modal.Header
            icon={
              <NodeIcon
                alt={`${scriptToEdit.provider} Icon`}
                imageSrc={scriptToEdit.icon_path}
                size="md"
              />
            }
          >
            {`Edit ${scriptToEdit.name}`}
          </Modal.Header>
          {editCatalogEntry ? (
            <SdkScriptConfigForm
              key={scriptToEdit.id}
              baseUrl={baseUrl}
              entry={editCatalogEntry}
              script={scriptToEdit}
              onClose={closeModal}
            />
          ) : (
            <Modal.Content>
              <div
                className="flex h-40 items-center justify-center font-inter-normal-12px text-gray-500"
                data-loc="edit-sdk-script-catalog-state"
              >
                {catalog.isPending ? (
                  <Spinner />
                ) : catalog.isError ? (
                  "Failed to load the SDK catalog. Please try again."
                ) : (
                  "This SDK Script's provider version is no longer available."
                )}
              </div>
            </Modal.Content>
          )}
        </Modal>
      )}

      <ConfirmationModal
        confirmationButtonText="Delete"
        description="Are you sure you want to delete this SDK bundle? This action is not reversible."
        open={modal.kind === "deleteBundle"}
        title={
          modal.kind === "deleteBundle"
            ? `Delete ${modal.bundle.name}`
            : "Delete SDK bundle"
        }
        variant="danger"
        onClose={closeModal}
        onConfirm={handleDeleteBundle}
      />

      <ConfirmationModal
        confirmationButtonText="Delete"
        description="Are you sure you want to delete this SDK Script? This action is not reversible."
        open={modal.kind === "deleteScript"}
        title={
          modal.kind === "deleteScript"
            ? `Delete ${modal.script.name}`
            : "Delete SDK Script"
        }
        variant="danger"
        onClose={closeModal}
        onConfirm={handleDeleteScript}
      />
    </>
  );
};
